import { useEffect, useState } from "react";
import { Stack, TextField } from "@mui/material";
import { ManualRefund } from "../../types/evidence";

const AMOUNT_ERROR = "Enter the amount refunded, more than zero.";
const DATE_ERROR = "Enter the refund date.";

const validAmount = (value: string): boolean => /^\d+(\.\d{1,2})?$/.test(value) && Number(value) > 0;
const validDate = (value: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(value);

// The receipt holds no refund, so the page prints the one the agent types beside the screenshot.
export default function RefundFields({ manualRefund, disabled, onSave, onUnsavedChange }: {
  manualRefund: ManualRefund | null;
  disabled: boolean;
  onSave: (value: ManualRefund | null) => Promise<boolean>;
  /** A half-typed or invalid pair is nothing a blur will save, so Generate waits for it like a malformed ARN. */
  onUnsavedChange: (unsaved: boolean) => void;
}) {
  const [amount, setAmount] = useState(manualRefund?.amount ?? "");
  const [refundedOn, setRefundedOn] = useState(manualRefund?.refundedOn ?? "");
  const [error, setError] = useState<"amount" | "date" | null>(null);
  const savedKey = JSON.stringify(manualRefund);
  useEffect(() => {
    const saved: ManualRefund | null = JSON.parse(savedKey);
    setAmount(saved?.amount ?? "");
    setRefundedOn(saved?.refundedOn ?? "");
    setError(null);
  }, [savedKey]);
  // Closed (the row unticked, or a run owns the draft), the fields fall back to what is saved, so
  // nothing half typed stays behind a field the agent can no longer reach.
  useEffect(() => {
    if (!disabled) return;
    const saved: ManualRefund | null = JSON.parse(savedKey);
    setAmount(saved?.amount ?? "");
    setRefundedOn(saved?.refundedOn ?? "");
    setError(null);
  }, [disabled, savedKey]);

  // A complete, valid pair is saved by the blur that clicking Generate causes; a half pair or a malformed
  // value is never sent, so that is what closes Generate.
  const nextAmount = amount.trim();
  const nextDate = refundedOn.trim();
  const unsaved = (nextAmount !== "" || nextDate !== "") && !(validAmount(nextAmount) && validDate(nextDate));
  // Gone from the page, the fields hold nothing back from Generate.
  useEffect(() => {
    onUnsavedChange(unsaved);
    return () => onUnsavedChange(false);
  }, [unsaved, onUnsavedChange]);

  const save = (value: ManualRefund | null) => {
    void onSave(value).then((ok) => {
      if (ok) return;
      setAmount(manualRefund?.amount ?? "");
      setRefundedOn(manualRefund?.refundedOn ?? "");
    });
  };
  const saveFields = () => {
    if (!nextAmount && !nextDate) {
      setError(null);
      if (manualRefund) save(null);
      return;
    }
    if (!nextAmount || !nextDate) return;
    if (!validAmount(nextAmount)) { setError("amount"); return; }
    if (!validDate(nextDate)) { setError("date"); return; }
    setError(null);
    if (nextAmount === manualRefund?.amount && nextDate === manualRefund?.refundedOn) return;
    save({ amount: nextAmount, refundedOn: nextDate });
  };

  return (
    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
      <TextField
        label="Amount refunded"
        size="small"
        value={amount}
        onChange={(event) => { setAmount(event.target.value); setError(null); }}
        onBlur={saveFields}
        disabled={disabled}
        error={error === "amount"}
        helperText={error === "amount" ? AMOUNT_ERROR : undefined}
        slotProps={{ htmlInput: { inputMode: "decimal" } }}
      />
      <TextField
        label="Refunded on"
        type="date"
        size="small"
        value={refundedOn}
        onChange={(event) => { setRefundedOn(event.target.value); setError(null); }}
        onBlur={saveFields}
        disabled={disabled}
        error={error === "date"}
        helperText={error === "date" ? DATE_ERROR : undefined}
        slotProps={{ inputLabel: { shrink: true } }}
      />
    </Stack>
  );
}
